import { useEffect, useMemo, useState } from 'react'
import { ArrowRight, Gauge, RefreshCw } from 'lucide-react'
import { Shell, useShellMeta } from '../../admin/Shell.js'
import { SettingsCrumb } from '../../admin/SettingsCrumb.js'
import { Select } from '../../admin/primitives.js'
import { NumberInput } from '../../admin/form.js'
import { Link } from '../../router.js'
import { lireSupervision, type Lecture } from '../api.js'
import { BandeauLecture, Pastille, fmtAge, fmtNombre, useVueSonde, type Ton } from '../vues/commun.js'
import { AutresEcrans } from './SupervisionGroupePage.js'
import { estSchema, fmtDateParis, lireNombre, type GroupeSchema } from './modele.js'
import './reglages.css'

/**
 * SUPERVISION › CONSOMMATION (écran 13, 21/10/2026) — `GET consumption`.
 *
 * Ce que coûte Sonde (conteneurs et leurs plafonds du compose, collecteur,
 * base, SSD, trafic et erreurs par source), l'économie réalisée sur Gestion
 * (écritures sur le vieux RAID avant / après), les budgets, et le simulateur
 * de cadence de Sonde (Ko/s ≈ Ko/s mesurés × cadence actuelle ÷ nouvelle).
 * Une mesure absente s'écrit « — », jamais 0 ; un budget sans mesure est gris.
 */

type N = number | null
interface Conso {
  computed_at: string
  age_s: N
  live: boolean
  summary_fr: string
  containers: { name: string; cpu_pct: N; mem_mb: N; limit_mem_mb: number; limit_cpus: number; mem_of_limit_pct: N }[]
  collector: { cpu_pct: N; rss_mb: N; el_delay_ms: N }
  database: { size_bytes: N; wal_bytes_24h: N; rows_written_24h: N; points_per_s: N }
  sources: { code: string; requests_per_min: N; kbytes_per_s: N; errors_last_hour: N; latency_ms_p50?: N; latency_ms_p95?: N; kbytes_per_request?: N }[]
  collectors?: { code: string; requests_per_min: N; kbytes_per_s: N; latency_ms_p50: N; cadence_evening_s: N; cadence_rest_s: N }[]
  simulator_fr?: string
  ssd: { written_bytes_per_day: N; days_measured: number; basis: string | null; wear_pct_per_year: N; endurance_bytes: number }
  gestion: {
    reference_bytes_per_day: number; reference_note_fr: string; first_week_bytes_per_day: N; last_week_bytes_per_day: N
    saved_bytes_per_day: N; saved_pct: N; daily: { day: string; bytes: number }[]
  }
  budgets: { key: string; label: string; unit: string; budget: number; actual: N; over: boolean | null }[]
}
const estConso = (x: unknown): x is Conso => {
  if (typeof x !== 'object' || x === null) return false
  const c = x as Conso
  return typeof c.computed_at === 'string' && typeof c.summary_fr === 'string' && Array.isArray(c.containers)
    && Array.isArray(c.sources) && Array.isArray(c.budgets) && typeof c.gestion === 'object' && c.gestion !== null
    && typeof c.ssd === 'object' && c.ssd !== null && typeof c.database === 'object' && c.database !== null
}

/** Octets → « 65 Mo », « 17,6 Go » ; « — » si absent. */
export function fmtOctets(n: N | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  const u = ['o', 'Ko', 'Mo', 'Go', 'To']
  let v = n, i = 0
  while (Math.abs(v) >= 1000 && i < u.length - 1) { v /= 1000; i++ }
  return `${v.toLocaleString('fr-FR', { maximumFractionDigits: v >= 100 ? 0 : 1 })} ${u[i]}`
}
const dec = (n: N | undefined, chiffres = 1, unite = '') => n === null || n === undefined || !Number.isFinite(n) ? '—'
  : `${n.toLocaleString('fr-FR', { maximumFractionDigits: chiffres })}${unite ? ` ${unite}` : ''}`

const tonBudget = (b: Conso['budgets'][number]): Ton => b.actual === null || b.over === null ? 'gris' : b.over ? 'warn' : 'ok'

export function SupervisionConsommationPage() {
  const meta = useShellMeta()
  const [rafraichi, setRafraichi] = useState<Lecture<Conso> | null>(null)
  const { lecture, precedente } = useVueSonde('consumption', estConso, 120_000)
  const [enCours, setEnCours] = useState(false)
  const [groupes, setGroupes] = useState<GroupeSchema[] | null>(null)
  useEffect(() => { void lireSupervision('/v/settings/schema', estSchema).then((l) => { if (l.etat === 'ok') setGroupes(l.data.groups) }) }, [])

  // Le document recalculé à la demande l'emporte tant qu'aucune relecture plus récente n'est arrivée.
  const doc = (rafraichi?.etat === 'ok' ? rafraichi.data : null) ?? (lecture?.etat === 'ok' ? lecture.data : precedente)
  const recalculer = async () => {
    setEnCours(true)
    setRafraichi(await lireSupervision('/v/consumption?refresh=1', estConso))
    setEnCours(false)
  }

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-page--management lc-stagger sv-page sv-reg-page">
        <SettingsCrumb fallback="/settings/supervision" />
        <header className="sv-reg-tete">
          <h1 className="lc-title cle-titre"><Gauge size={22} aria-hidden="true" /> Consommation de Sonde</h1>
          <p className="lc-subtitle">Ce que coûte la supervision au serveur, et ce qu’elle fait économiser à Gestion.</p>
          <p className="sv-reg-tete__meta">
            <span>{doc ? `${doc.live ? 'Calculé à l’instant' : `Calculé ${fmtAge(doc.age_s)}`} (${fmtDateParis(doc.computed_at)})` : '—'}</span>
            <span>Source : Sonde</span>
            <button type="button" className="lc-btn lc-btn--ghost" disabled={enCours} onClick={() => { void recalculer() }}>
              <RefreshCw size={14} aria-hidden="true" /> {enCours ? 'Calcul…' : 'Recalculer maintenant'}
            </button>
          </p>
        </header>
        <BandeauLecture lecture={lecture} depuis={precedente?.computed_at ?? null} />
        {rafraichi && rafraichi.etat !== 'ok' ? <BandeauLecture lecture={rafraichi} /> : null}
        {!doc && !lecture ? <p className="lc-hint sv-section">Lecture de Sonde…</p> : null}
        {doc ? <Contenu d={doc} /> : null}
        {groupes ? <AutresEcrans groupes={groupes} actuel="consommation" /> : null}
      </main>
    </Shell>
  )
}

function Contenu({ d }: { d: Conso }) {
  return (
    <>
      <p className="sv-conseq sv-section">{d.summary_fr}</p>

      <section className="lc-panel sv-section" aria-labelledby="sv-conso-budgets">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="sv-conso-budgets">Budgets</h2>
          <Link className="lc-hint" href="/settings/supervision/consumption">Régler les budgets <ArrowRight size={13} aria-hidden="true" /></Link>
        </header>
        <div className="lc-panel__body">
          {d.budgets.length === 0 ? <p className="lc-hint">Aucun budget publié.</p> : (
            <ul className="sv-conso-budgets">
              {d.budgets.map((b) => {
                const ratio = b.actual !== null && b.budget > 0 ? Math.min(1, b.actual / b.budget) : null
                const ton = tonBudget(b)
                return (
                  <li key={b.key} className="sv-conso-budget">
                    <div className="sv-conso-budget__tete">
                      <span className="sv-conso-budget__label">{b.label}</span>
                      <Pastille ton={ton}>{b.actual === null ? 'pas encore mesuré' : b.over ? 'au-dessus du budget' : 'dans le budget'}</Pastille>
                    </div>
                    <p className="sv-conso-budget__chiffre"><strong>{dec(b.actual, 2)}</strong> / {dec(b.budget, 2)} {b.unit}</p>
                    <span className="sv-conso-jauge" aria-hidden="true">
                      <span className={`sv-conso-jauge__plein sv-conso-jauge__plein--${ton}`} style={{ width: ratio === null ? '0%' : `${Math.round(ratio * 100)}%` }} />
                    </span>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </section>

      <div className="sv-deux">
        <section className="lc-panel" aria-labelledby="sv-conso-gestion">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="sv-conso-gestion">Économie réalisée sur Gestion</h2></header>
          <div className="lc-panel__body cle-corps">
            <dl className="cle-faits">
              <div><dt>Avant Sonde (référence)</dt><dd>{fmtOctets(d.gestion.reference_bytes_per_day)} par jour</dd></div>
              <div><dt>Première semaine</dt><dd>{d.gestion.first_week_bytes_per_day === null ? '—' : `${fmtOctets(d.gestion.first_week_bytes_per_day)} par jour`}</dd></div>
              <div><dt>Dernière semaine</dt><dd>{d.gestion.last_week_bytes_per_day === null ? '—' : `${fmtOctets(d.gestion.last_week_bytes_per_day)} par jour`}</dd></div>
              <div><dt>Économie</dt><dd>{d.gestion.saved_bytes_per_day === null ? '— (pas encore mesurée)' : `${fmtOctets(d.gestion.saved_bytes_per_day)} par jour${d.gestion.saved_pct !== null ? ` (${dec(d.gestion.saved_pct, 0)} %)` : ''}`}</dd></div>
            </dl>
            <p className="lc-hint">{d.gestion.reference_note_fr}</p>
            {d.gestion.daily.length > 0 ? <Barres jours={d.gestion.daily} reference={d.gestion.reference_bytes_per_day} /> : null}
          </div>
        </section>
        <section className="lc-panel" aria-labelledby="sv-conso-ssd">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="sv-conso-ssd">SSD et base de Sonde</h2></header>
          <div className="lc-panel__body cle-corps">
            <dl className="cle-faits">
              <div><dt>Écrit par jour</dt><dd>{fmtOctets(d.ssd.written_bytes_per_day)}{d.ssd.basis ? ` (mesure : ${d.ssd.basis === 'wal' ? 'journal de la base' : 'compteurs des conteneurs'})` : ''}</dd></div>
              <div><dt>Usure du SSD</dt><dd>{d.ssd.wear_pct_per_year === null ? '—' : `${dec(d.ssd.wear_pct_per_year, 2)} % par an`}</dd></div>
              <div><dt>Jours mesurés</dt><dd>{d.ssd.days_measured}</dd></div>
              <div><dt>Taille de la base</dt><dd>{fmtOctets(d.database.size_bytes)}</dd></div>
              <div><dt>Journal (WAL) sur 24 h</dt><dd>{fmtOctets(d.database.wal_bytes_24h)}</dd></div>
              <div><dt>Lignes écrites sur 24 h</dt><dd>{fmtNombre(d.database.rows_written_24h)}</dd></div>
              <div><dt>Points par seconde</dt><dd>{dec(d.database.points_per_s, 1)}</dd></div>
            </dl>
          </div>
        </section>
      </div>

      <section className="lc-panel sv-section" aria-labelledby="sv-conso-conteneurs">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="sv-conso-conteneurs">Conteneurs de Sonde</h2>
          <span className="lc-hint">plafonds du fichier compose : affichés, non modifiables ici</span>
        </header>
        <div className="lc-panel__body">
          <p className="lc-hint">Collecteur : CPU {dec(d.collector.cpu_pct, 1, '%')} · mémoire {dec(d.collector.rss_mb, 0, 'Mo')} · retard de boucle {dec(d.collector.el_delay_ms, 0, 'ms')}</p>
          <div className="sv-conso-table" role="table" aria-label="Conteneurs">
            <div className="sv-conso-table__ligne sv-conso-table__tete" role="row">
              <span role="columnheader">Conteneur</span><span role="columnheader">CPU</span><span role="columnheader">Mémoire</span><span role="columnheader">Plafond</span>
            </div>
            {d.containers.map((c) => (
              <div key={c.name} className="sv-conso-table__ligne" role="row">
                <span role="cell" className="sv-mono">{c.name}</span>
                <span role="cell" data-titre="CPU">{dec(c.cpu_pct, 1, '%')}</span>
                <span role="cell" data-titre="Mémoire">{dec(c.mem_mb, 0, 'Mo')}{c.mem_of_limit_pct !== null ? ` (${dec(c.mem_of_limit_pct, 0)} %)` : ''}</span>
                <span role="cell" data-titre="Plafond">{dec(c.limit_mem_mb, 0, 'Mo')} · {dec(c.limit_cpus, 2)} cœur</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="lc-panel sv-section" aria-labelledby="sv-conso-sources">
        <header className="lc-panel__head">
          <h2 className="lc-section-title" id="sv-conso-sources">Trafic par source (dernière heure)</h2>
        </header>
        <div className="lc-panel__body">
          {d.sources.length === 0 ? <p className="lc-hint">Aucune source mesurée.</p> : (
            <div className="sv-conso-table sv-conso-table--sources" role="table" aria-label="Trafic par source">
              <div className="sv-conso-table__ligne sv-conso-table__tete" role="row">
                <span role="columnheader">Source</span><span role="columnheader">Requêtes</span><span role="columnheader">Débit</span>
                <span role="columnheader">Latence</span><span role="columnheader">Erreurs</span>
              </div>
              {d.sources.map((x) => (
                <div key={x.code} className="sv-conso-table__ligne" role="row">
                  <span role="cell" className="sv-mono">{x.code}</span>
                  <span role="cell" data-titre="Requêtes">{dec(x.requests_per_min, 1, '/min')}</span>
                  <span role="cell" data-titre="Débit">{dec(x.kbytes_per_s, 2, 'Ko/s')}</span>
                  <span role="cell" data-titre="Latence">{dec(x.latency_ms_p50, 0)} / {dec(x.latency_ms_p95, 0, 'ms')}</span>
                  <span role="cell" data-titre="Erreurs">
                    {x.errors_last_hour === null ? '—' : x.errors_last_hour > 0 ? <Pastille ton="warn">{x.errors_last_hour}</Pastille> : '0'}
                  </span>
                </div>
              ))}
            </div>
          )}
          <p className="lc-hint">Latence : médiane / 95e centile.</p>
        </div>
      </section>

      {d.collectors && d.collectors.length > 0 ? <Simulateur collecteurs={d.collectors} texte={d.simulator_fr ?? null} /> : null}
    </>
  )
}

/** Écritures de Gestion par jour, avec la ligne de référence d'avant Sonde. */
function Barres({ jours, reference }: { jours: { day: string; bytes: number }[]; reference: number }) {
  const max = Math.max(reference, ...jours.map((j) => j.bytes)) || 1
  const derniers = jours.slice(-30)
  return (
    <figure className="sv-conso-barres">
      <div className="sv-conso-barres__zone" role="img" aria-label={`Écritures de Gestion par jour : ${derniers.map((j) => `${j.day.slice(8, 10)}/${j.day.slice(5, 7)} ${fmtOctets(j.bytes)}`).join(', ')}`}>
        <span className="sv-conso-barres__ref" style={{ bottom: `${(reference / max) * 100}%` }} />
        {derniers.map((j) => <span key={j.day} className="sv-conso-barres__barre" style={{ height: `${Math.max(2, (j.bytes / max) * 100)}%` }} title={`${j.day} : ${fmtOctets(j.bytes)}`} />)}
      </div>
      <figcaption className="lc-hint">Écritures de Gestion par jour ; la ligne marque la référence d’avant Sonde.</figcaption>
    </figure>
  )
}

/** « Passer <collecteur> à N s économise X Ko/s » : la formule de Sonde, sans rien enregistrer. */
function Simulateur({ collecteurs, texte }: { collecteurs: NonNullable<Conso['collectors']>; texte: string | null }) {
  const tries = useMemo(() => [...collecteurs].sort((a, b) => (b.kbytes_per_s ?? 0) - (a.kbytes_per_s ?? 0)), [collecteurs])
  const [code, setCode] = useState(tries[0]?.code ?? '')
  const [mode, setMode] = useState<'evening' | 'rest'>('evening')
  const [saisie, setSaisie] = useState('')
  const c = tries.find((x) => x.code === code) ?? tries[0]
  if (!c) return null
  const actuelle = mode === 'evening' ? c.cadence_evening_s : c.cadence_rest_s
  const nouvelle = lireNombre(saisie)
  const estime = c.kbytes_per_s !== null && actuelle !== null && nouvelle !== null && Number.isFinite(nouvelle) && nouvelle > 0
    ? c.kbytes_per_s * actuelle / nouvelle : null
  const ecart = estime !== null && c.kbytes_per_s !== null ? c.kbytes_per_s - estime : null
  const famille = c.code.split('.')[0] ?? ''
  return (
    <section className="lc-panel sv-section" aria-labelledby="sv-conso-simu">
      <header className="lc-panel__head">
        <h2 className="lc-section-title" id="sv-conso-simu">Simulateur de cadence</h2>
        <span className="lc-hint">rien n’est enregistré</span>
      </header>
      <div className="lc-panel__body cle-corps">
        <div className="sv-conso-simu">
          <div className="sv-jour-filtre"><span className="lc-label">Collecteur</span>
            <Select value={c.code} onChange={setCode} ariaLabel="Collecteur" options={tries.map((x) => ({ value: x.code, label: `${x.code} · ${dec(x.kbytes_per_s, 3, 'Ko/s')}` }))} /></div>
          <div className="sv-jour-filtre"><span className="lc-label">Mode</span>
            <Select value={mode} onChange={(v) => setMode(v === 'rest' ? 'rest' : 'evening')} ariaLabel="Mode"
                    options={[{ value: 'evening', label: `Soirée (${dec(c.cadence_evening_s, 0, 's')})` }, { value: 'rest', label: `Repos (${dec(c.cadence_rest_s, 0, 's')})` }]} /></div>
          <div className="sv-jour-filtre"><span className="lc-label">Nouvelle cadence</span>
            <NumberInput value={saisie} onChange={setSaisie} unit="s" min={1} step={1} ariaLabel="Nouvelle cadence en secondes" /></div>
        </div>
        <p className="sv-conseq">
          {actuelle === null ? 'Ce collecteur ne tourne pas dans ce mode.'
            : estime === null ? `Aujourd’hui : ${dec(c.kbytes_per_s, 3, 'Ko/s')} toutes les ${actuelle} s. Saisissez une cadence pour estimer.`
              : `Passer ${c.code} à ${nouvelle} s ${ecart !== null && ecart >= 0 ? `économise environ ${dec(ecart, 3, 'Ko/s')}` : `coûte environ ${dec(ecart === null ? null : -ecart, 3, 'Ko/s')} de plus`} (${dec(estime, 3, 'Ko/s')} au lieu de ${dec(c.kbytes_per_s, 3, 'Ko/s')}).`}
        </p>
        {texte ? <p className="lc-hint">{texte}</p> : null}
        <p><Link className="lc-btn" href={`/settings/supervision/cadence${famille ? `?onglet=${encodeURIComponent(famille)}` : ''}`}>Régler les cadences <ArrowRight size={14} aria-hidden="true" /></Link></p>
      </div>
    </section>
  )
}
